import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, articles, siteInfo } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const filteredArticles = articles.filter((a) => a.category === currentCategory.slug);

  const webPageSchema = {
    name: `${currentCategory.name} - 91片`,
    description: `91片${currentCategory.name}分类，${currentCategory.description}。海量正版高清${currentCategory.name}资源，极速播放。`,
    url: `https://www.91pian.com/category/${currentCategory.slug}`,
    inLanguage: 'zh-CN',
    isPartOf: { '@type': 'WebSite', name: '91片', url: 'https://www.91pian.com' },
  };

  return (
    <>
      <SchemaOrg type="WebPage" data={webPageSchema} />

      <div className="h-16" />
      <Navbar />

      <main className="min-h-screen bg-background-50">
        {/* Category Hero */}
        <section className="relative w-full h-[320px] md:h-[400px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={currentCategory.image}
              alt={currentCategory.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
          </div>
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
            <span className="flex items-center justify-center w-14 h-14 rounded-full bg-primary-500/80 mb-4">
              <i className={`${currentCategory.icon} text-white text-2xl`} />
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-white">
              {currentCategory.name}
            </h1>
            <p className="mt-3 text-white/70 text-sm md:text-base">
              {currentCategory.description}
            </p>
          </div>
        </section>

        {/* Breadcrumb + Category Tabs */}
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { name: '首页', href: '/' },
              { name: currentCategory.name },
            ]}
          />

          {/* Category Tabs */}
          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  cat.slug === currentCategory.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-foreground-700 hover:bg-background-100 border border-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Article List */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-white rounded-lg overflow-hidden hover:bg-background-50 transition-colors"
                >
                  <div className="aspect-video overflow-hidden bg-background-200">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h2 className="font-heading font-semibold text-foreground-950 text-sm md:text-base group-hover:text-primary-500 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="mt-2 text-xs md:text-sm text-foreground-600 line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-foreground-500">
                      <span>{article.publishDate}</span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-600">该分类暂无内容，请浏览其他分类</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}